import { useAtomValue, useSetAtom } from "jotai";
import { useAccessibility, useAuth } from "common/hooks";

import {
  SignUpOnboarding,
  useSignUpProfilePayload,
} from "@/features/onboarding";
import { AuthModalScreen, useAuthModals } from "@/hooks/useAuthModals";
import { registeredUserIdAtom, submitAccessibilityAtom } from "@/store/atoms";

interface SignUpOnboardingComponentProps {
  onClose: () => void;
}

/**
 * The onboarding questions between creating an account and verifying it
 * (`16150:279245`) — replacing the old five-screen "Profile setup" wizard.
 *
 * Where the answers go depends on how the account was made:
 *
 * - **Email sign-up** is not signed in yet, so the answers are handed to
 *   `VerifyEmailComponent`, which posts them against the new account's id when
 *   it mounts — the same hand-off the old wizard used. "Onboarding questions
 *   come first before verification," per the frame's note.
 * - **Social sign-up** is signed in the moment the account exists and has no
 *   email to verify, so the answers are posted here and the modal closes. The
 *   old wizard closed without posting them at all on this path.
 *
 * The verify screen is still the email-link one. The frame's six-digit code
 * screen waits on the backend.
 */
const SignUpOnboardingComponent = ({
  onClose,
}: SignUpOnboardingComponentProps) => {
  const { setScreenIndex } = useAuthModals();
  const { isLoggedIn } = useAuth();
  const registeredUserId = useAtomValue(registeredUserIdAtom);
  const setSubmitData = useSetAtom(submitAccessibilityAtom);
  const { submitAccessibility } = useAccessibility();
  const buildPayload = useSignUpProfilePayload();

  const handleComplete = () => {
    const payload = buildPayload(registeredUserId);

    if (isLoggedIn) {
      submitAccessibility(payload);
      onClose();
      return;
    }

    setSubmitData(payload);
    setScreenIndex(AuthModalScreen.VerifyEmail);
  };

  return <SignUpOnboarding onComplete={handleComplete} />;
};

export default SignUpOnboardingComponent;
